import { IconButton, IconName } from '@optimus-web/core';
import { LocalizedText, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { TooltipWrapper } from '../../TooltipWrapper';
import { useInvalidateUserProfile } from '../../../hooks/useInvalidateUserProfile';

interface Props {
    disabled: boolean;
    enterpriseId: string;
    propertyName: string;
    userId: string;
    onDelete?: () => void;
}

export const DeletePropertyCell: React.FC<Props> = ({ disabled, enterpriseId, propertyName, userId, onDelete }) => {
    // this component gets unmounted before callbacks in mutate are triggered -> we need to pass them to the mutation config as a workaround
    const unassignProperty = useUnassignProperty({ propertyName, userId, onSuccess: onDelete });

    return (
        <TooltipWrapper
            active={disabled}
            tooltip={<LocalizedText textKey={TextKey.UserProfileDeletePropertyCellTooltip} />}
        >
            <IconButton
                icon={IconName.Delete}
                disabled={disabled}
                onClick={() => {
                    unassignProperty.mutate({ enterpriseIds: [enterpriseId], userId, unassign: true });
                }}
                size="small"
                variant="ghost"
                type="submit"
            />
        </TooltipWrapper>
    );
};

const useUnassignProperty = ({
    propertyName,
    onSuccess,
    userId,
}: {
    propertyName: string;
    onSuccess?: () => void;
    userId: string;
}) => {
    const invalidateUserProfile = useInvalidateUserProfile();

    return useRepositoryMutation('userProfiles', 'updateProperties', {
        onSuccess: () => {
            onSuccess?.();
            invalidateUserProfile(userId);

            showSuccessAlert({
                description: (
                    <LocalizedText
                        textKey={TextKey.UserProfileUnassignPropertySuccessMessageDescription}
                        substitutes={{ property: propertyName }}
                    />
                ),
                title: (
                    <LocalizedText
                        textKey={TextKey.UserProfileUnassignPropertySuccessMessageTitle}
                        substitutes={{ property: propertyName }}
                    />
                ),
            });
        },
    });
};
